function recapSvgMarkup(
  title: string,
  subtitle: string,
  rows: readonly { name: string; score: string; winner: boolean }[],
): { svg: string; height: number } {
  const height = Math.max(420, 206 + rows.length * 58);
  const escape = (value: string) =>
    value.replace(/[&<>"']/g, (character) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&apos;',
    }[character]!));
  const content = rows
    .map((row, index) => {
      const y = 220 + index * 58;
      const fill = row.winner ? '#f7bd4f' : '#f3efff';
      const dot = row.winner ? '<circle cx="62" cy="-7" r="7" fill="#f7bd4f" />' : '';
      return `<g transform="translate(0 ${y})">${dot}<text x="84" y="0" fill="${fill}" font-family="ui-sans-serif, system-ui" font-size="24" font-weight="700">${escape(row.name)}</text><text x="636" y="0" fill="${fill}" text-anchor="end" font-family="ui-sans-serif, system-ui" font-size="26" font-weight="800">${escape(row.score)}</text><path d="M52 22H648" stroke="#ffffff" stroke-opacity=".12" /></g>`;
    })
    .join('');
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="700" height="${height}" viewBox="0 0 700 ${height}"><defs><linearGradient id="bg" x1="0" y1="0" x2="1" y2="1"><stop stop-color="#0c3f40"/><stop offset=".55" stop-color="#122d3e"/><stop offset="1" stop-color="#241541"/></linearGradient><filter id="glow"><feGaussianBlur stdDeviation="22"/></filter></defs><rect width="700" height="${height}" rx="40" fill="url(#bg)"/><circle cx="614" cy="72" r="120" fill="#b850d6" fill-opacity=".28" filter="url(#glow)"/><circle cx="72" cy="${height - 48}" r="96" fill="#27c6b8" fill-opacity=".22" filter="url(#glow)"/><text x="52" y="66" fill="#f7bd4f" font-family="ui-sans-serif, system-ui" font-size="18" font-weight="800" letter-spacing="3">RUMMY · SESSION RECAP</text><text x="52" y="122" fill="#ffffff" font-family="ui-sans-serif, system-ui" font-size="38" font-weight="800">${escape(title)}</text><text x="52" y="160" fill="#d7e5e8" font-family="ui-sans-serif, system-ui" font-size="20">${escape(subtitle)}</text><path d="M52 188H648" stroke="#ffffff" stroke-opacity=".22"/>${content}</svg>`;
  return { svg, height };
}

/**
 * A compact, offline-first share card. SVG keeps type sharp in messaging apps
 * without needing a canvas or a third-party image renderer.
 */
export function createSessionRecap(
  title: string,
  subtitle: string,
  rows: readonly { name: string; score: string; winner: boolean }[],
): File {
  const { svg } = recapSvgMarkup(title, subtitle, rows);
  return new File([svg], 'rummy-session-recap.svg', { type: 'image/svg+xml' });
}

/**
 * The same card, rasterized to PNG.
 *
 * A raw SVG attachment is the thing most likely to make the existing share
 * feature *not* actually land anywhere: WhatsApp, Instagram and Telegram
 * routinely fail to preview it or refuse it outright, where a PNG is
 * universally handled. Returns null rather than throwing — SVG stays the
 * share path's fallback, not this.
 */
export async function createSessionRecapPng(
  title: string,
  subtitle: string,
  rows: readonly { name: string; score: string; winner: boolean }[],
): Promise<File | null> {
  try {
    const { svg, height } = recapSvgMarkup(title, subtitle, rows);
    const svgBlob = new Blob([svg], { type: 'image/svg+xml' });
    const svgUrl = URL.createObjectURL(svgBlob);
    try {
      const image = await new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('svg-decode-failed'));
        img.src = svgUrl;
      });
      // 2x for a card that stays sharp on a high-density phone screen, which
      // is where nearly all of these get shared from.
      const scale = 2;
      const canvas = document.createElement('canvas');
      canvas.width = 700 * scale;
      canvas.height = height * scale;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      ctx.scale(scale, scale);
      ctx.drawImage(image, 0, 0, 700, height);
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
      if (!blob) return null;
      return new File([blob], 'rummy-session-recap.png', { type: 'image/png' });
    } finally {
      URL.revokeObjectURL(svgUrl);
    }
  } catch {
    return null;
  }
}
